import React from 'react';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from '../../../i18n';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../utils/languageUtils';

interface DateTimeRangeFilterItemProps {
    label: string;
    value?: [string | null, string | null] | null;
    onChange?: (dates: [string | null, string | null] | null) => void;
    className?: string;
    spanClassName?: string;
    placeholder?: [string, string];
    format?: string;
    showTime?: boolean | object;
    allowClear?: boolean;
    size?: 'small' | 'middle' | 'large';
    disabled?: boolean;
    hideLabel?: boolean;
}

const DateTimeRangeFilterItem: React.FC<DateTimeRangeFilterItemProps> = ({
    label,
    value,
    onChange,
    className = '',
    spanClassName = '',
    placeholder,
    format = 'YYYY/MM/DD HH:mm:ss',
    showTime = {
        format: 'HH:mm:ss',
        defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
    },
    allowClear = true,
    size = 'small',
    disabled = false,
    hideLabel = false
}) => {
    // 将字符串转换为 Dayjs 对象以供 DatePicker 使用
    // 添加类型检查，确保 value 是有效的数组格式
    const dayjsValue = value && Array.isArray(value) && value.length === 2 ? [
        value[0] ? dayjs(value[0]) : null,
        value[1] ? dayjs(value[1]) : null
    ] as [Dayjs | null, Dayjs | null] : null;

    // 处理 DatePicker 的变更，将 Dayjs 转换为 ISO 字符串
    const handleChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        if (onChange) {
            if (dates && dates[0] && dates[1]) {
                // 转换为 ISO 字符串格式
                const startISO = dates[0].format('YYYY-MM-DD HH:mm:ss');
                const endISO = dates[1].format('YYYY-MM-DD HH:mm:ss');
                onChange([startISO, endISO]);
            } else {
                onChange(null);
            }
        }
    };

    // 默认占位符
    const defaultPlaceholder: [string, string] = [
        t('components.input.start') || 'Start Time',
        t('components.input.end') || 'End Time'
    ];

    // 使用统一的样式 Hook，根据语言自动返回合适的样式类名
    const containerClassName = useFilterContainerClassName(className);
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className={containerClassName}>
            {!hideLabel && (
                <span className={labelClassName}>
                    {label}
                </span>
            )}
            <div className={inputContainerClassName}>
                <DatePicker.RangePicker
                    value={dayjsValue}
                    onChange={handleChange}
                    className="w-full h-8"
                    placeholder={placeholder || defaultPlaceholder}
                    format={format}
                    showTime={showTime}
                    size={size}
                    allowClear={allowClear}
                    disabled={disabled}
                // 禁用未来日期（可选）
                // disabledDate={(current) => current && current > dayjs().endOf('day')}
                />
            </div>
        </div>
    );
};

export default DateTimeRangeFilterItem;
